import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { ProblemDiaryEntry } from '../../../types/businessPlan';

type Location = ProblemDiaryEntry['location'];

interface ProblemDiaryProps {
  initialEntries?: ProblemDiaryEntry[];
  onSave: (entries: ProblemDiaryEntry[]) => void;
}

const LOCATIONS: Location[] = ['School', 'Home', 'Somewhere else'];
const MAX_ENTRIES = 7;

export default function ProblemDiary({ initialEntries = [], onSave }: ProblemDiaryProps) {
  const [entries, setEntries] = useState<ProblemDiaryEntry[]>(initialEntries);
  const [currentUgh, setCurrentUgh] = useState('');
  const [currentLocation, setCurrentLocation] = useState<Location | ''>('');
  const [viewIndex, setViewIndex] = useState(-1); // -1 = new entry form

  useEffect(() => {
    if (initialEntries.length > 0 && entries.length === 0) {
      setEntries(initialEntries);
    }
  }, [initialEntries, entries.length]);

  const canSaveEntry = currentUgh.trim().length > 0 && currentLocation !== '';

  const handleSaveEntry = () => {
    if (!canSaveEntry) return;
    const newEntry: ProblemDiaryEntry = {
      ughMoment: currentUgh.trim(),
      location: currentLocation as Location,
    };
    const updated = [...entries, newEntry];
    setEntries(updated);
    onSave(updated);
    setCurrentUgh('');
    setCurrentLocation('');
    setViewIndex(updated.length - 1);
  };

  const handleDeleteEntry = (idx: number) => {
    const updated = entries.filter((_, i) => i !== idx);
    setEntries(updated);
    onSave(updated);
    if (viewIndex >= updated.length) {
      setViewIndex(updated.length > 0 ? updated.length - 1 : -1);
    }
  };

  const handleAddAnother = () => {
    setViewIndex(-1);
    setCurrentUgh('');
    setCurrentLocation('');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-auto max-w-md"
    >
      <h2 className="text-center text-2xl font-bold text-gray-900">Problem Diary</h2>
      <p className="mt-1 text-center text-sm text-gray-500">
        Log your &quot;ugh moments&quot; (min 1, max {MAX_ENTRIES})
      </p>

      {/* Entry counter */}
      <div className="mt-4 flex items-center justify-center gap-2 text-sm">
        <span className={entries.length >= 1 ? 'text-green-600' : 'text-gray-400'}>
          {entries.length >= 1 ? '✓' : '○'}
        </span>
        <span className="text-gray-600">
          {entries.length} / {MAX_ENTRIES} entries
        </span>
      </div>

      {/* Entry navigation */}
      {entries.length > 0 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          {entries.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setViewIndex(idx)}
              className={`h-8 w-8 rounded-full text-xs font-bold transition-all ${
                viewIndex === idx
                  ? 'bg-brand-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {idx + 1}
            </button>
          ))}
          {entries.length < MAX_ENTRIES && (
            <button
              onClick={handleAddAnother}
              className={`h-8 w-8 rounded-full text-lg transition-all ${
                viewIndex === -1
                  ? 'bg-brand-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              +
            </button>
          )}
        </div>
      )}

      <AnimatePresence mode="wait">
        {/* Viewing a saved entry */}
        {viewIndex >= 0 && viewIndex < entries.length && (
          <motion.div
            key={`view-${viewIndex}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="mt-4 rounded-2xl border-2 border-amber-200 bg-amber-50 p-5"
            style={{
              backgroundImage: 'repeating-linear-gradient(transparent, transparent 27px, #e5e5e5 28px)',
              backgroundPosition: '0 40px',
            }}
          >
            <div className="border-l-2 border-red-300 pl-4">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                Entry {viewIndex + 1}
              </p>
              <p className="mt-2 text-gray-900">{entries[viewIndex].ughMoment}</p>
              <p className="mt-2 text-sm text-gray-500">
                📍 {entries[viewIndex].location}
              </p>
            </div>
            <button
              onClick={() => handleDeleteEntry(viewIndex)}
              className="mt-3 text-sm text-red-400 hover:text-red-600"
            >
              Delete entry
            </button>
          </motion.div>
        )}

        {/* New entry form */}
        {viewIndex === -1 && (
          <motion.div
            key="new-entry"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="mt-4 rounded-2xl border-2 border-gray-200 bg-white p-5"
            style={{
              backgroundImage: 'repeating-linear-gradient(transparent, transparent 27px, #f0f0f0 28px)',
              backgroundPosition: '0 40px',
            }}
          >
            <div className="border-l-2 border-red-300 pl-4">
              <label className="text-sm font-medium text-gray-700">
                What was the &quot;Ugh!&quot; moment?
              </label>
              <textarea
                value={currentUgh}
                onChange={(e) => setCurrentUgh(e.target.value)}
                placeholder="That moment when..."
                rows={3}
                className="mt-1 w-full resize-none rounded-lg border-0 bg-transparent p-0 text-gray-900 placeholder-gray-300 focus:outline-none focus:ring-0"
                style={{ minHeight: 80 }}
              />

              <label className="mt-4 block text-sm font-medium text-gray-700">
                Where did it happen?
              </label>
              <div className="mt-2 flex flex-wrap gap-2">
                {LOCATIONS.map((loc) => (
                  <button
                    key={loc}
                    onClick={() => setCurrentLocation(loc)}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${
                      currentLocation === loc
                        ? 'bg-brand-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {loc}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleSaveEntry}
              disabled={!canSaveEntry}
              className="mt-4 w-full rounded-xl bg-brand-500 py-2.5 font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Save Entry
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
